import { useCallback, useMemo } from 'react';
import { useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { notification } from 'antd';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { scheduleActions, scheduleSelectors } from 'features/schedule/redux';
import { getSlotKey, getSuggestedDropSlotKeys, getUniqueBells } from 'features/schedule/utils';
import { WEEK_DAYS } from 'features/schedule/model';
import { checkTimeslotViolation } from 'features/schedule/utils/checkTimeslotViolation';
import type { ScheduleLesson, DayOfWeek } from 'services/api';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

export const useScheduleRow = () => {
  const dispatch = useAppDispatch();

  const schedule = useAppSelector(scheduleSelectors.getSchedule);
  const highlightedErrors = useAppSelector(scheduleSelectors.getHighlightedErrors);
  const currentBellSchedule = useAppSelector(scheduleSelectors.getCurrentBellSchedule);
  const suggestions = useAppSelector(scheduleSelectors.getSuggestions);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const bells = useMemo(
    () => getUniqueBells(currentBellSchedule?.bells || []),
    [currentBellSchedule?.bells],
  );

  const lessonBySlot = useMemo(() => {
    const result: Record<string, ScheduleLesson[]> = {};

    schedule?.forEach((scheduleItem) => {
      scheduleItem.timeslots.forEach((timeslot) => {
        const slotKey = getSlotKey(
          timeslot.dayOfWeek,
          timeslot.startTime,
          timeslot.endTime,
          scheduleItem.educationalClass.id,
        );

        if (!result[slotKey]) {
          result[slotKey] = [];
        }

        result[slotKey].push(...timeslot.lessons);
      });
    });

    return result;
  }, [schedule]);

  const gridRows = useMemo(
    () =>
      WEEK_DAYS.flatMap((day) =>
        bells.map(({ startTime, endTime }, index) => {
          const cells = schedule.map((scheduleItem) => {
            const slotKey = getSlotKey(day, startTime, endTime, scheduleItem.educationalClass.id);
            const educationalClass = scheduleItem.educationalClass;
            const { isError, category, errors } = checkTimeslotViolation({
              dayOfWeek: day,
              startTime,
              endTime,
              educationalClassId: educationalClass.id,
              highlightedErrors,
            });

            return {
              slotKey,
              day,
              startTime,
              endTime,
              item: lessonBySlot[slotKey],
              isSuggested: suggestions?.includes(slotKey) || false,
              isError,
              category,
              errors,
              educationalClass,
            };
          });

          return {
            key: `${day}-${startTime}-${endTime}`,
            day,
            startTime,
            endTime,
            isFirstLessonInDay: index === 0,
            isLastLessonInDay: index === bells.length - 1,
            isLastDay: day === WEEK_DAYS[WEEK_DAYS.length - 1],
            cells,
          };
        }),
      ),
    [bells, schedule, lessonBySlot, highlightedErrors, suggestions],
  );

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const sourceSlotKey = active.data.current?.slotKey;
      const lessonId = active.data.current?.lessonId;

      const suggestedSlotKeys = getSuggestedDropSlotKeys({
        gridRows,
        lessonBySlot,
        sourceSlotKey,
        lessonId,
      });

      dispatch(scheduleActions.setSuggestions(suggestedSlotKeys));

      if (!suggestedSlotKeys.length) {
        notification.info({
          message: 'Нет свободных ячеек',
          description: 'Для этого урока нет свободных ячеек без конфликта по учителю или кабинету',
        });
      }
    },
    [dispatch, gridRows, lessonBySlot],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      dispatch(scheduleActions.clearSuggestions());

      if (!over) return;

      const sourceSlotKey = active.data.current?.slotKey;
      const targetSlotKey = over.id as string;
      const lessonId = active.data.current?.lessonId;

      if (!sourceSlotKey || !targetSlotKey || sourceSlotKey === targetSlotKey || !lessonId) {
        return;
      }

      const [, , , sourceClassId] = sourceSlotKey.split('-');
      const [targetDay, targetStartTime, targetEndTime, targetClassId] = targetSlotKey.split(
        '-',
      ) as [DayOfWeek, string, string, string];

      if (sourceClassId !== targetClassId) {
        return;
      }

      dispatch(
        scheduleActions.applyDraftLessonPatch({
          lessonId,
          patch: {
            timeslot: {
              dayOfWeek: targetDay,
              startTime: targetStartTime,
              endTime: targetEndTime,
              educationalClassId: Number(targetClassId),
            },
          },
        }),
      );
    },
    [dispatch],
  );

  const handleDragCancel = useCallback(() => {
    dispatch(scheduleActions.clearSuggestions());
  }, [dispatch]);

  return {
    state: {
      sensors,
      bells,
      gridRows,
    },
    methods: { handleDragStart, handleDragEnd, handleDragCancel },
  };
};
